import React, { useState } from "react";
import { useParams, Navigate, Link, useNavigate } from "react-router-dom";
import SEO from "./SEO";
import Navbar from "./layout/Navbar";
import Footer from "./layout/Footer";
import FloatingActions from "./layout/FloatingActions";
import { detailedCourses } from "../data/detailedCoursesData";
import useFavicon from "../hooks/useFavicon";
import LazyImage from "./ui/LazyImage";

// Helper function to map course category to dynamic favicon path
const getFaviconPath = (category) => {
  if (!category) return "/logo-main.png";
  const catLower = category.toLowerCase();
  if (catLower.includes("junior")) return "/junior academy-favicon/web-app-manifest-512x512.png";
  if (catLower.includes("business")) return "/business-lab-favicon/web-app-manifest-512x512.png";
  if (catLower.includes("elevare")) return "/elevare academy-favicon/web-app-manifest-512x512.png";
  return "/logo-main.png";
};

export default function CoursePage() {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);

  // Fetch course details
  const course = detailedCourses[courseSlug];

  // Set category-specific dynamic favicon
  useFavicon(course ? getFaviconPath(course.category) : "/logo-main.png");

  if (!course) {
    return <Navigate to="/404" replace />;
  }

  const isJunior = course.category.toLowerCase().includes("junior");
  const isBusiness = course.category.toLowerCase().includes("business");

  const canonicalUrl = `https://cervelloeduverse.com/course/${courseSlug}`;

  const categoryPath = isJunior
    ? "/junior-academy"
    : (isBusiness ? "/business-lab" : "/elevare");

  const courseSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": isBusiness ? "Service" : "Course",
        "@id": `${canonicalUrl}#course`,
        "name": course.title,
        "description": course.description,
        "provider": {
          "@type": "EducationalOrganization",
          "name": "Cervello Eduverse",
          "url": "https://cervelloeduverse.com",
          "logo": "https://cervelloeduverse.com/images/cervello-logo.webp",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Santoshnagar-Saidabad Main road, 5th Floor, Emaan Manzil, Royal Embassy School Lane, beside Success Group of Institutions",
            "addressLocality": "Hyderabad",
            "addressRegion": "Telangana",
            "postalCode": "500059",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 17.3553111,
            "longitude": 78.5057216
          }
        },
        ...(isBusiness ? {
          "serviceType": "Startup Incubation & Business Consulting"
        } : {
          "courseCode": courseSlug,
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": course.mode,
            "duration": course.duration
          }
        })
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonicalUrl}#breadcrumb`,
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cervelloeduverse.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": course.category,
            "item": `https://cervelloeduverse.com${categoryPath}`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": course.title,
            "item": canonicalUrl
          }
        ]
      }
    ]
  };

  const toggleFAQ = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <React.Fragment>
      <SEO
        title={`${course.title} – Detailed Syllabus & Mentorship | Cervello Eduverse`}
        description={course.description.substring(0, 155)}
        url={canonicalUrl}
        canonical={canonicalUrl}
        faqData={course.faqs}
        schemaData={courseSchema}
      />

      <div className="font-inter bg-nm-light text-[#1E293B] min-h-screen relative">
        <Navbar />

        {/* HERO SECTION */}
        <section className="relative overflow-hidden pt-16 pb-20 bg-nm-dark text-[#0B1C2D] border-b border-slate-200/20">
          {/* Subtle Background Image Overlay */}
          {course.images && course.images.length > 0 && (
            <div className="absolute inset-0 z-0 select-none pointer-events-none">
              {/* Mobile background (hidden on desktop) */}
              {/* Mobile background (hidden on desktop) */}
              <LazyImage
                src={course.bgMobile || course.bgDesktop || course.images[0]}
                alt={`${course.title} Course Mobile Banner`}
                className="block md:hidden w-full h-full object-cover opacity-45"
                fetchPriority="high"
                placeholderBg="bg-transparent"
              />
              {/* Desktop background (hidden on mobile) */}
              <LazyImage
                src={course.bgDesktop || course.images[0]}
                alt={`${course.title} Course Desktop Banner`}
                className="hidden md:block w-full h-full object-cover opacity-45"
                fetchPriority="high"
                placeholderBg="bg-transparent"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1C2D] via-[#0B1C2D]/55 to-transparent" />
            </div>
          )}

          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff02_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none z-10" />
          <div className="absolute bottom-0 right-0 w-[450px] h-[450px] bg-[#C6A25E]/10 rounded-full blur-[130px] translate-y-1/3 translate-x-1/3 pointer-events-none z-10" />

          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <div className="max-w-3xl">
              {/* Category Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-6 border border-transparent backdrop-blur-md transition-all duration-300 ${isJunior
                ? "glass-card-dark text-sky-400"
                : (isBusiness
                   ? "glass-card-dark text-cyan-400"
                   : "glass-card-dark text-[#C6A25E]")
                }`}>
                <span className={`w-2 h-2 rounded-full animate-pulse ${isJunior ? "bg-sky-400" : (isBusiness ? "bg-cyan-400" : "bg-[#C6A25E]")
                  }`} />
                {course.category}
              </div>

              <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                {course.title}
              </h1>

              <p className="text-slate-700 text-base sm:text-lg md:text-xl font-light mb-8 max-w-2xl">
                {course.tagline}
              </p>

              {/* Quick specifications grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 sm:p-5 rounded-2xl glass-card-dark border-transparent mb-8 text-xs sm:text-sm text-[#0B1C2D]">
                <div>
                  <span className="block text-slate-600 font-medium text-[10px] uppercase tracking-wider">{isBusiness ? 'Est. Timeline' : 'Duration'}</span>
                  <span className="font-semibold text-[#0B1C2D] mt-1 block">{course.duration}</span>
                </div>
                <div>
                  <span className="block text-slate-600 font-medium text-[10px] uppercase tracking-wider">{isBusiness ? 'Engagement Format' : 'Learning Mode'}</span>
                  <span className="font-semibold text-[#0B1C2D] mt-1 block">{course.mode}</span>
                </div>
                <div>
                  <span className="block text-slate-600 font-medium text-[10px] uppercase tracking-wider">{isBusiness ? 'Target Level' : 'Skill Level'}</span>
                  <span className="font-semibold text-[#0B1C2D] mt-1 block">{course.level}</span>
                </div>
                <div>
                  <span className="block text-slate-600 font-medium text-[10px] uppercase tracking-wider">{isBusiness ? 'Milestone Reviews' : 'Evaluation'}</span>
                  <span className="font-semibold text-[#0B1C2D] mt-1 block">{isBusiness ? 'Weekly Project Syncs' : 'Weekly Practical'}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a
                  href={`/book-demo?vertical=${isJunior ? 'junior' : (isBusiness ? 'business' : 'elevare')}`}
                  className="px-8 py-4 rounded-xl glass-button-dark text-[#0B1C2D] font-bold border-transparent active:scale-[0.98] transition-all text-center"
                >
                  {isJunior ? '🚀 Book Free 1-Day Demo' : (isBusiness ? '💼 Request Consultation (Free)' : '🚀 Book ₹99 Demo Session (7-Days)')}
                </a>
                <a
                  href="#syllabus"
                  className="px-8 py-4 rounded-xl glass-button-dark text-slate-700 font-bold border-transparent active:scale-[0.98] transition-all text-center"
                >
                  {isBusiness ? 'View Service Roadmap' : 'View Curriculum'}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* OVERVIEW SECTION */}
        <section className="py-20 bg-nm-light">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              {/* Description */}
              <div className="lg:col-span-7 space-y-6">
                <h2 className="font-playfair text-3xl font-bold text-[#0B1C2D]">{isBusiness ? 'Service Overview' : 'Course Overview'}</h2>
                <p className="text-slate-600 text-base leading-relaxed font-light">
                  {course.description}
                </p>

                {/* Highlights List */}
                <div className="pt-6">
                  <h3 className="text-base font-bold uppercase tracking-wider text-slate-400 mb-4">{isBusiness ? 'Service Highlights' : 'Course Highlights'}</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {course.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 nm-inset-light rounded-xl border-transparent">
                        <span className="w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold text-xs">✓</span>
                        <span className="text-sm font-semibold text-slate-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar Info */}
              <div className="lg:col-span-5 nm-flat-light p-8 rounded-3xl border-transparent">
                <h3 className="font-bold text-[#0B1C2D] text-lg mb-6 flex items-center gap-2">
                  <span className="text-xl">🛠️</span> {isBusiness ? 'Tools & Platforms covered' : 'Tools & Tech covered'}
                </h3>

                {course.tools && course.tools.length > 0 ? (
                  <div className="flex flex-wrap gap-2.5 mb-8">
                    {course.tools.map((tool, idx) => (
                      <span key={idx} className="px-3.5 py-2 rounded-xl nm-inset-light border-transparent text-xs font-semibold text-slate-700 hover:text-[#C6A25E] transition-colors">
                        {tool}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-xs mb-8">Bespoke study materials and evaluative datasets are standard.</p>
                )}

                <h3 className="font-bold text-[#0B1C2D] text-lg mb-4 flex items-center gap-2">
                  <span className="text-xl">🎯</span> {isBusiness ? 'Key Deliverables' : 'Learning Outcomes'}
                </h3>
                <ul className="space-y-3">
                  {course.outcomes.map((outcome, idx) => (
                    <li key={idx} className="flex gap-2 text-xs sm:text-sm text-slate-600 leading-relaxed font-light">
                      <span className="text-emerald-500 font-bold mt-0.5">•</span>
                      <span>{outcome}</span>
                    </li>
                  ))}
                </ul>

                {course.requirements && course.requirements.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-slate-200/30">
                    <h3 className="font-bold text-[#0B1C2D] text-lg mb-4 flex items-center gap-2">
                      <span className="text-xl">📋</span> Requirements
                    </h3>
                    <ul className="space-y-3">
                      {course.requirements.map((req, idx) => (
                        <li key={idx} className="flex gap-2 text-xs sm:text-sm text-slate-600 leading-relaxed font-light">
                          <span className="text-[#C6A25E] font-bold mt-0.5">•</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CURRICULUM SYLLABUS ROADMAP */}
        <section id="syllabus" className="py-20 bg-nm-light border-y border-slate-200/40">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                {isBusiness ? 'Roadmap' : 'Curriculum'} <span className="bg-gradient-to-r from-[#C6A25E] to-sky-500 bg-clip-text text-transparent">Timeline</span>
              </h2>
              <p className="text-slate-500 text-sm">
                {isBusiness ? 'Detailed step-by-step phases of our strategic execution.' : 'Detailed step-by-step phases of our concept modules.'}
              </p>
            </div>

            <div className="relative border-l-2 border-[#C6A25E]/30 pl-8 space-y-10">
              {course.syllabus.map((step, idx) => (
                <div key={idx} className="relative group">
                  {/* Timeline Node */}
                  <div className="absolute -left-[41px] top-1.5 w-6 h-6 rounded-full bg-[#eef2f7] border-4 border-[#C6A25E] flex items-center justify-center transition-transform group-hover:scale-110">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#0B1C2D]" />
                  </div>

                  <div className="nm-flat-light p-6 sm:p-8 rounded-2xl border-transparent hover:-translate-y-0.5 transition-all">
                    <span className="text-xs font-bold text-[#C6A25E] tracking-widest uppercase">{step.phase}</span>
                    <h3 className="text-lg font-bold text-[#0B1C2D] mt-1 mb-2">{step.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed font-light">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SPECIFIC TESTIMONIALS / REVIEW IF PRESENT */}
        {course.reviews && course.reviews.length > 0 && (
          <section className="py-20 bg-nm-light">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-4">Alumni Experience</span>
              <div className="nm-inset-light p-8 sm:p-12 rounded-3xl border-transparent relative">
                <span className="text-6xl text-[#C6A25E]/20 absolute top-4 left-6 font-serif">“</span>
                <p className="text-base sm:text-lg md:text-xl text-slate-700 italic relative z-10 leading-relaxed font-playfair font-medium">
                  {course.reviews[0].text}
                </p>
                <div className="mt-8 flex flex-col items-center">
                  <h4 className="font-bold text-[#0B1C2D]">{course.reviews[0].name}</h4>
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mt-1">{course.reviews[0].role}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ACCORDION FAQS */}
        {course.faqs && course.faqs.length > 0 && (
          <section className="py-20 bg-nm-light border-t border-slate-200/40">
            <div className="max-w-3xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-[#0B1C2D]">{isBusiness ? 'Service FAQs' : 'Course FAQs'}</h2>
              </div>

              <div className="space-y-4">
                {course.faqs.map((faq, index) => {
                  const isOpen = activeFaq === index;
                  return (
                    <div key={index} className="nm-flat-light rounded-2xl border-transparent overflow-hidden">
                      <button
                        className="w-full text-left p-5 flex justify-between items-center bg-transparent hover:bg-slate-200/30 transition-colors focus:outline-none"
                        onClick={() => toggleFAQ(index)}
                        aria-expanded={isOpen}
                      >
                        <h3 className="font-bold text-sm sm:text-base text-[#0B1C2D]">{faq.question}</h3>
                        <span className={`text-[#C6A25E] text-base transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                          ▾
                        </span>
                      </button>

                      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-40 opacity-100 border-t border-slate-200/20' : 'max-h-0 opacity-0 pointer-events-none'
                        }`}>
                        <div className="p-5 text-slate-600 text-xs sm:text-sm leading-relaxed">
                          {faq.answer}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* FINAL CTA ENROLL BOX */}
        <section className="py-20 bg-nm-dark text-[#0B1C2D] text-center relative overflow-hidden border-t border-slate-200/20">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000001_1px,transparent_1px),linear-gradient(to_bottom,#00000001_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" />

          <div className="max-w-3xl mx-auto px-6 relative z-10">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#0B1C2D]">Ready to Build Your Capabilities?</h2>
            <p className="text-slate-600 text-sm max-w-md mx-auto mb-8 font-light">
              Reserve your slot in our live trial cycles. Experience conceptual clarity, personal feedback, and launch targets first-hand.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={`/book-demo?vertical=${isJunior ? 'junior' : (isBusiness ? 'business' : 'elevare')}`}
                className="px-8 py-4 rounded-xl nm-button-dark text-[#0B1C2D] font-bold border-transparent active:scale-[0.98] transition-all text-center w-full sm:w-auto"
              >
                {isJunior ? 'Register Free 1-Day Demo' : (isBusiness ? 'Request Consultation (Free)' : 'Register Demo Session (₹99)')}
              </a>
              <Link
                to={course.category === 'Junior Academy' ? '/junior-academy' :
                  course.category === 'Business Lab' ? '/business-lab' : '/elevare'}
                className="px-8 py-4 rounded-xl nm-button-dark text-slate-700 font-bold border-transparent active:scale-[0.98] transition-all text-center w-full sm:w-auto text-sm"
              >
                {isBusiness ? '← Back to Services Overview' : '← Back to Program Overview'}
              </Link>
            </div>
          </div>
        </section>

        <Footer />
        <FloatingActions />
      </div>
    </React.Fragment>
  );
}
